import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for auto-saving form data to localStorage
 * Saves every 30 seconds and on form changes
 * 
 * @param {string} formKey - Unique key for this form (e.g., 'add-company-draft')
 * @param {object} formData - Current form data to save
 * @param {boolean} enabled - Whether auto-save is enabled
 * @returns {object} - { loadDraft, clearDraft, lastSaved }
 */
export const useAutoSave = (formKey, formData, enabled = true) => {
  const lastSavedRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Save to localStorage
  const saveDraft = useCallback(() => {
    if (!enabled || !formKey) return;
    
    try {
      const draftData = {
        data: formData,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(formKey, JSON.stringify(draftData));
      lastSavedRef.current = new Date();
      console.log(`✓ Draft saved: ${formKey}`);
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [formKey, formData, enabled]);

  // Load draft from localStorage
  const loadDraft = () => {
    try {
      const saved = localStorage.getItem(formKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.data;
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
    return null;
  };

  // Clear draft from localStorage
  const clearDraft = () => {
    try {
      localStorage.removeItem(formKey);
      console.log(`✓ Draft cleared: ${formKey}`);
    } catch (error) {
      console.error('Error clearing draft:', error);
    }
  };

  // Auto-save effect - saves every 30 seconds
  useEffect(() => {
    if (!enabled) return;

    // Save on data change after 2 seconds
    if (formData && Object.keys(formData).length > 0) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        saveDraft();
      }, 2000);
    }

    // Also save every 30 seconds
    const intervalId = setInterval(() => {
      saveDraft();
    }, 30000);

    return () => {
      clearInterval(intervalId);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [formData, enabled, saveDraft]);

  return {
    loadDraft,
    clearDraft,
    lastSaved: lastSavedRef.current
  };
};

/**
 * Get formatted time since last save
 */
export const getTimeSince = (date) => {
  if (!date) return null;
  
  const seconds = Math.floor((new Date() - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};
